import { formatCount } from '@/components/FilterPills';
import { PageShell } from '@/components/PageShell';
import { UserChip } from '@/components/UserChip';
import { DomainEventsTable } from '@/app/domainEvents/DomainEventsTable';
import { DraftBadge, PublishedBadge } from '@/components/badges/Common';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useRowClick } from '@/lib/navigation';
import { BLOCKING_QUERY_FLAGS, QUERY_FLAG_LABELS, QUERY_FLAGS } from '@/app/considerations/appliesToStatus';
import { stalledRecalls, untypedRecallCount } from '@/app/recalls/recallStatus';
import {
    activeHomesCountState,
    brandStatusCountsState,
    considerationQueryCountsState,
    equipmentCountForSpaceState,
    equipmentTotalCountState,
    modelStatusCountsState,
    recallCountsState,
    recallsStateDocState,
    recallVerdictCountsState,
    recentDomainEventsState,
    recentHomesState,
    recentUsersState,
    spacesState,
    stuckRecallCandidatesState,
    usersCountState,
} from '@/state';
import { useAsyncAtomValue, useFreshOnMountAtomValue, useLoadableAtomValue } from '@/state/loadableHooks';
import { formatRelative } from '@/util/dates';
import type { Space, SpaceId } from '@rock/types';
import type { Atom } from 'jotai';
import { orderBy } from 'lodash';
import { ChevronRight } from 'lucide-react';
import { type ReactNode, useMemo } from 'react';
import { Link } from 'react-router-dom';

const DashboardPage = () => {
    const spaces = useAsyncAtomValue(spacesState, []);
    const equipmentCount = useAsyncAtomValue(equipmentTotalCountState);
    const sortedSpaces = useMemo(() => orderBy(spaces, ['name']), [spaces]);

    return (
        <PageShell>
            <header>
                <h1 className="text-3xl font-semibold">Dashboard</h1>
                <div className="page-description">Overview of activity across homes and spaces in Rock.</div>
            </header>

            {/* Two tiers: the plain platform counts, then the catalogs — whose review breakdowns need
                    the extra width, and whose height no longer stretches the plain cards' empty space. */}
            <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <StatCard
                    title="Users"
                    to="/directory/users"
                    countAtom={usersCountState}
                    description="Everyone who has signed into the app."
                />
                <StatCard
                    title="Homes"
                    to="/directory/homes"
                    countAtom={activeHomesCountState}
                    description="Active homes."
                />
                <StatCard
                    title="Equipment"
                    countAtom={equipmentTotalCountState}
                    description="Captured across all homes."
                />
            </section>

            <section className="grid gap-4 lg:grid-cols-2">
                <ModelsStatCard />
                <BrandsStatCard />
                <RecallsStatCard />
                <ConsiderationQueriesCard />
            </section>

            <section className="grid gap-4 lg:grid-cols-2">
                <RecentHomes />
                <RecentUsers />
            </section>

            <section>
                <Card>
                    <CardHeader>
                        <SectionTitle to="/system/domain-events">Recent Activity</SectionTitle>
                        <CardDescription>The latest domain events across all entities.</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <RecentActivity />
                    </CardContent>
                </Card>
            </section>

            <section className="grid gap-4 lg:grid-cols-[1.4fr_1fr]">
                <Card>
                    <CardHeader>
                        <SectionTitle>Space Status</SectionTitle>
                        <CardDescription>Track publishing state and system names by space.</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="w-[180px]">Space</TableHead>
                                    <TableHead>Status</TableHead>
                                    <TableHead className="hidden md:table-cell">System title</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {sortedSpaces.map((space) => (
                                    <SpaceRow key={space.id} space={space} />
                                ))}
                                {sortedSpaces.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={3} className="py-8 text-center text-muted-foreground">
                                            No spaces found yet.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>
                <EquipmentBySpace spaces={sortedSpaces} totalEquipmentCount={equipmentCount ?? 0} />
            </section>
        </PageShell>
    );
};

/** Uppercase card-title label used on every dashboard card; pass `to` to link it to its full page. */
const SectionTitle = ({ to, children }: { to?: string; children: ReactNode }) => {
    const label = 'text-sm font-semibold uppercase tracking-wider text-muted-foreground';
    if (!to) {
        return <CardTitle className={label}>{children}</CardTitle>;
    }
    return (
        <CardTitle className={label}>
            <Link to={to} className="group inline-flex items-center gap-1 transition-colors hover:text-foreground">
                {children}
                <ChevronRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </Link>
        </CardTitle>
    );
};

/** A number from a one-shot aggregation atom: ellipsis while loading, em-dash if the query failed. */
const CountValue = ({ countAtom }: { countAtom: Atom<Promise<number>> }) => {
    const [count, loadable] = useLoadableAtomValue(countAtom);
    return <>{loadable.state === 'hasError' ? '—' : (count ?? '…')}</>;
};

const StatCard = ({
    title,
    to,
    countAtom,
    description,
}: {
    title: string;
    to?: string;
    countAtom: Atom<Promise<number>>;
    description: string;
}) => (
    <Card>
        <CardHeader className="pb-2">
            <SectionTitle to={to}>{title}</SectionTitle>
        </CardHeader>
        <CardContent>
            <div className="text-3xl font-semibold">
                <CountValue countAtom={countAtom} />
            </div>
            <div className="field-hint">{description}</div>
        </CardContent>
    </Card>
);

/**
 * A catalog card: the collection's size as the hero, plus a breakdown of the rows waiting on someone.
 * Hero and breakdown sit side by side (they wrap when the card is narrow) so the breakdown's labels stay
 * next to their numbers instead of stretching across the card.
 */
const CatalogCard = ({
    title,
    to,
    count,
    description,
    children,
}: {
    title: string;
    to: string;
    count: ReactNode;
    description: string;
    children: ReactNode;
}) => (
    <Card>
        <CardHeader className="pb-2">
            <SectionTitle to={to}>{title}</SectionTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap items-start justify-between gap-x-8 gap-y-3">
            <div>
                <div className="text-3xl font-semibold">{count}</div>
                <div className="field-hint">{description}</div>
            </div>
            <div className="flex min-w-[220px] flex-1 flex-col sm:max-w-[300px]">{children}</div>
        </CardContent>
    </Card>
);

/**
 * The Models hero: catalog size plus the acquisition pipeline, counted server-side off the models' own
 * `docStatus` rollup — the same stored field the list filters on, so each row's number is exactly how
 * many models its link lands on. Rows deep-link into the list's URL-persisted status filter.
 */
const ModelsStatCard = () => {
    const { value: counts, failed } = useFreshOnMountAtomValue(modelStatusCountsState);
    return (
        <CatalogCard
            title="Models"
            to="/products/models"
            count={failed ? '—' : (counts?.all ?? '…')}
            description={`In the catalog${counts?.archived ? ` · ${counts.archived} archived` : ''}.`}
        >
            <BreakdownRow
                label="Needs review"
                count={counts?.['needs-review']}
                settled={!!counts || failed}
                to="/products/models?status=needs-review"
                emphasisClass="text-amber-600"
            />
            {/* Settled models that got new information (a new home, a brand) — a second review queue. */}
            <BreakdownRow
                label="Revisit"
                count={counts?.revisit}
                settled={!!counts || failed}
                to="/products/models?status=revisit"
                emphasisClass="text-violet-700"
            />
            <BreakdownRow
                label="Searching"
                count={counts?.searching}
                settled={!!counts || failed}
                to="/products/models?status=searching"
            />
            <BreakdownRow
                label="Search failed"
                count={counts?.['search-failed']}
                settled={!!counts || failed}
                to="/products/models?status=search-failed"
                emphasisClass="text-destructive"
            />
            <BreakdownRow
                label="Parked"
                count={counts?.parked}
                settled={!!counts || failed}
                to="/products/models?status=parked"
            />
            {/* Brand proposals the enrichment couldn't prove. */}
            <BreakdownRow
                label="Needs brand review"
                count={counts?.['brand-review']}
                settled={!!counts || failed}
                to="/products/models?status=brand-review"
                emphasisClass="text-amber-600"
            />
        </CatalogCard>
    );
};

/**
 * The Brands hero: catalog size plus the curation queue, counted server-side off each brand's stored
 * `reviewStatus` — the same field the brands list filters on, so every row's number is exactly how many
 * rows its link lands on. Junk brands are held out of the hero (they're catalogued noise, not brands)
 * the way archived models are, and named in the description instead.
 */
const BrandsStatCard = () => {
    const { value: brands, failed: error } = useFreshOnMountAtomValue(brandStatusCountsState);
    return (
        <CatalogCard
            title="Brands"
            to="/products/brands"
            count={error ? '—' : (brands?.live ?? '…')}
            description={`In the catalog${brands?.junk ? ` · ${brands.junk} junk` : ''}.`}
        >
            <BreakdownRow
                label="Unreviewed"
                count={brands?.unreviewed}
                settled={!!brands || error}
                to="/products/brands?status=unreviewed"
                emphasisClass="text-amber-600"
            />
            <BreakdownRow
                label="Needs attention"
                count={brands?.['needs-attention']}
                settled={!!brands || error}
                to="/products/brands?status=needs-attention"
                emphasisClass="text-amber-600"
            />
            {/* Not queues — the curation progress the two rows above are counted against. */}
            <BreakdownRow
                label="Approved"
                count={brands?.approved}
                settled={!!brands || error}
                to="/products/brands?status=approved"
            />
            <BreakdownRow
                label="No-name"
                count={brands?.['no-name']}
                settled={!!brands || error}
                to="/products/brands?status=no-name"
            />
        </CatalogCard>
    );
};

/** A sync older than this is worth a warning: the daily schedule has missed at least one run. */
const RECALL_SYNC_STALE_MS = 2 * 24 * 60 * 60 * 1000;

/**
 * The Recalls hero: the review queue (drafts) as the headline, the status breakdown and catalog gaps as
 * rows that deep-link into the Recalls tabs and their filters, the live impact figures (units affected / possibly
 * affected across every home — written from Phase 2), and the last sync with a muted warning when it is
 * stale or its count guard tripped. Server count aggregations throughout, except the stalled row (a live
 * query cut by age); nothing is denormalized for it.
 */
const RecallsStatCard = () => {
    const { value: counts, failed } = useFreshOnMountAtomValue(recallCountsState);
    const { value: verdicts, failed: verdictsFailed } = useFreshOnMountAtomValue(recallVerdictCountsState);
    const state = useAsyncAtomValue(recallsStateDocState);
    const [candidates, candidatesLoadable] = useLoadableAtomValue(stuckRecallCandidatesState);
    const stalled = candidates && stalledRecalls(candidates);
    const syncAgeMs = state?.lastSyncAt ? Date.now() - new Date(state.lastSyncAt).getTime() : undefined;
    const syncWarning =
        !state?.lastSyncAt || (syncAgeMs !== undefined && syncAgeMs > RECALL_SYNC_STALE_MS)
            ? 'no sync in two days'
            : state.lastSyncOutcome === 'skipped'
              ? 'last sync tripped the count guard'
              : state.lastSyncOutcome === 'error'
                ? 'last sync ended with errors'
                : undefined;
    return (
        <CatalogCard
            title="Recalls"
            to="/products/recalls/catalog?status=draft"
            count={formatCount(counts?.byStatus.draft, !!counts || failed)}
            description={`Awaiting review · ${counts?.all !== undefined ? `${counts.all} recall(s) on file` : '…'}.`}
        >
            <BreakdownRow
                label="Drafts with matched units"
                count={counts?.withMatches.draft}
                settled={!!counts || failed}
                to="/products/recalls/catalog?status=draft&matches=1"
                emphasisClass="text-amber-600"
            />
            <BreakdownRow
                label="Re-review pending"
                count={counts?.reReview}
                settled={!!counts || failed}
                to="/products/recalls/catalog?rereview=1"
                emphasisClass="text-amber-600"
            />
            <BreakdownRow
                label="New suggestions"
                count={counts?.suggestions}
                settled={!!counts || failed}
                to="/products/recalls/catalog?suggestions=1"
                emphasisClass="text-violet-700"
            />
            <BreakdownRow
                label="Approved"
                count={counts?.byStatus.approved}
                settled={!!counts || failed}
                to="/products/recalls/catalog?status=approved"
            />
            <BreakdownRow
                label="No matched type"
                count={untypedRecallCount(state)}
                settled={!!state}
                to="/products/recalls/no-matched-type"
                emphasisClass="text-violet-700"
            />
            <BreakdownRow
                label="Stalled before review"
                count={stalled?.length}
                settled={!!stalled || candidatesLoadable.state === 'hasError'}
                to="/products/recalls/stalled"
                emphasisClass="text-amber-600"
            />
            <BreakdownRow
                label="Rejected"
                count={counts?.byStatus.rejected}
                settled={!!counts || failed}
                to="/products/recalls/rejected"
            />
            <BreakdownRow
                label="Affected verdicts (all time)"
                count={verdicts?.affected}
                settled={!!verdicts || verdictsFailed}
                to="/products/recalls/catalog?status=approved"
                emphasisClass="text-destructive"
            />
            <BreakdownRow
                label="Possibly affected verdicts (all time)"
                count={verdicts?.possiblyAffected}
                settled={!!verdicts || verdictsFailed}
                to="/products/recalls/catalog?status=approved"
                emphasisClass="text-amber-600"
            />
            <BreakdownRow
                label="Units notified"
                count={verdicts?.notified}
                settled={!!verdicts || verdictsFailed}
                to="/products/recalls/catalog?status=approved"
            />
            <div className="mt-2 text-xs text-muted-foreground">
                Last sync {state?.lastSyncAt ? formatRelative(state.lastSyncAt) : 'never'}
                {state?.lastSyncCount !== undefined ? ` · ${state.lastSyncCount} fetched` : ''}
                {syncWarning && <span className="text-amber-600"> · {syncWarning}</span>}
            </div>
        </CatalogCard>
    );
};

/**
 * Approved consideration types, and every reason one isn't reaching homes as it should: red rows reach
 * no home at all, amber rows wait on a review. Counted from the same flags the list filters on, so each
 * row's number is exactly how many rows its link lands on.
 */
const ConsiderationQueriesCard = () => {
    const [counts, loadable] = useLoadableAtomValue(considerationQueryCountsState);
    const error = loadable.state === 'hasError';
    return (
        <CatalogCard
            title="Consideration Queries"
            to="/content/considerations"
            count={error ? '—' : (counts?.approved ?? '…')}
            description="Approved consideration types."
        >
            {QUERY_FLAGS.map((flag) => (
                <BreakdownRow
                    key={flag}
                    label={QUERY_FLAG_LABELS[flag]}
                    count={counts?.flags[flag]}
                    settled={!!counts || error}
                    to={`/content/considerations?query=${flag}`}
                    emphasisClass={BLOCKING_QUERY_FLAGS.includes(flag) ? 'text-destructive' : 'text-amber-600'}
                />
            ))}
        </CatalogCard>
    );
};

/** One breakdown line in a catalog card — emphasized only when its count is non-zero. */
const BreakdownRow = ({
    label,
    count,
    settled,
    to,
    emphasisClass,
}: {
    label: string;
    count: number | undefined;
    /** The card's counts have arrived (or all failed): a missing count is then a failed one, not a pending one. */
    settled: boolean;
    to: string;
    emphasisClass?: string;
}) => (
    <Link
        to={to}
        className="-mx-1 flex items-center justify-between rounded px-1 py-0.5 text-sm transition-colors hover:bg-muted/60"
    >
        <span className="text-muted-foreground">{label}</span>
        <span className={`font-medium tabular-nums ${count && emphasisClass ? emphasisClass : ''}`}>
            {formatCount(count, settled)}
        </span>
    </Link>
);

const RecentHomes = () => {
    const homes = useAsyncAtomValue(recentHomesState);
    const onRowClick = useRowClick();
    return (
        <Card>
            <CardHeader>
                <SectionTitle to="/directory/homes">Recent Homes</SectionTitle>
                <CardDescription>The most recently created homes.</CardDescription>
            </CardHeader>
            <CardContent>
                {!homes || homes.length === 0 ? (
                    <div className="section-description">{homes ? 'No homes created yet.' : 'Loading…'}</div>
                ) : (
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Home</TableHead>
                                <TableHead>Owner</TableHead>
                                <TableHead className="text-right">Created</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {homes.map((home) => (
                                <TableRow
                                    key={home.id}
                                    className="cursor-pointer transition-colors hover:bg-muted/50"
                                    onClick={onRowClick(`/directory/homes/${encodeURIComponent(home.id)}`)}
                                >
                                    <TableCell className="font-medium">
                                        <div className="flex flex-col">
                                            <span>{home.name}</span>
                                            <span className="text-xs text-muted-foreground">{home.zip}</span>
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <UserChip userId={home.owner} showEmail />
                                    </TableCell>
                                    <TableCell className="text-right text-muted-foreground">
                                        {formatRelative(home.createdAt)}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </CardContent>
        </Card>
    );
};

const RecentUsers = () => {
    const users = useAsyncAtomValue(recentUsersState);
    const onRowClick = useRowClick();
    return (
        <Card>
            <CardHeader>
                <SectionTitle to="/directory/users">Recent Users</SectionTitle>
                <CardDescription>The most recently active accounts.</CardDescription>
            </CardHeader>
            <CardContent>
                {!users || users.length === 0 ? (
                    <div className="section-description">{users ? 'No users signed up yet.' : 'Loading…'}</div>
                ) : (
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>User</TableHead>
                                <TableHead className="text-right">Last login</TableHead>
                                <TableHead className="text-right">Created</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {users.map((user) => (
                                <TableRow
                                    key={user.id}
                                    className="cursor-pointer transition-colors hover:bg-muted/50"
                                    onClick={onRowClick(`/directory/users/${encodeURIComponent(user.id)}`)}
                                >
                                    <TableCell className="font-medium">
                                        <div className="flex flex-col">
                                            <span>{user.name || user.email || user.id}</span>
                                            {user.name && user.email && (
                                                <span className="text-xs text-muted-foreground">{user.email}</span>
                                            )}
                                        </div>
                                    </TableCell>
                                    <TableCell className="text-right text-muted-foreground">
                                        {formatRelative(user.lastLoginAt)}
                                    </TableCell>
                                    <TableCell className="text-right text-muted-foreground">
                                        {formatRelative(user.createdAt)}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </CardContent>
        </Card>
    );
};

const RecentActivity = () => {
    const events = useAsyncAtomValue(recentDomainEventsState, []);
    return <DomainEventsTable events={events} />;
};

const SpaceRow = ({ space }: { space: Space }) => (
    <TableRow key={space.id} className="hover:bg-muted/50">
        <TableCell className="font-medium">
            <div className="flex flex-col">
                <span>{space.name}</span>
                <span className="text-xs text-muted-foreground">{space.id}</span>
            </div>
        </TableCell>
        <TableCell>{space.published ? <PublishedBadge /> : <DraftBadge />}</TableCell>
        <TableCell className="hidden md:table-cell text-muted-foreground">{space.systemTitle || '—'}</TableCell>
    </TableRow>
);

/** Per-space equipment counts via server aggregations — one count query per space, no home docs read. */
const EquipmentBySpace = ({ spaces, totalEquipmentCount }: { spaces: Space[]; totalEquipmentCount: number }) => (
    <Card>
        <CardHeader>
            <SectionTitle>Equipment by Space</SectionTitle>
            <CardDescription>Where equipment has been documented across homes.</CardDescription>
        </CardHeader>
        <CardContent>
            {spaces.length === 0 ? (
                <div className="text-sm text-muted-foreground">No equipment activity recorded yet.</div>
            ) : (
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Space</TableHead>
                            <TableHead className="text-right">Equipment</TableHead>
                            <TableHead className="hidden sm:table-cell text-right">Share</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {spaces.map((space) => (
                            <EquipmentBySpaceRow
                                key={space.id}
                                spaceId={space.id}
                                name={space.name}
                                totalEquipmentCount={totalEquipmentCount}
                            />
                        ))}
                    </TableBody>
                </Table>
            )}
        </CardContent>
    </Card>
);

const EquipmentBySpaceRow = ({
    spaceId,
    name,
    totalEquipmentCount,
}: {
    spaceId: SpaceId;
    name: string;
    totalEquipmentCount: number;
}) => {
    const [count, loadable] = useLoadableAtomValue(equipmentCountForSpaceState(spaceId));
    return (
        <TableRow>
            <TableCell className="font-medium">{name}</TableCell>
            <TableCell className="text-right">{loadable.state === 'hasError' ? '—' : (count ?? '…')}</TableCell>
            <TableCell className="hidden sm:table-cell text-right text-muted-foreground">
                {count !== undefined && totalEquipmentCount > 0
                    ? `${Math.round((count / totalEquipmentCount) * 100)}%`
                    : '—'}
            </TableCell>
        </TableRow>
    );
};

export default DashboardPage;
